import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { MessageService, MenuItem } from 'primeng/api';
import { MerchantService, StoreService, AppDataService, ProductService } from 'app/services';
import { XpressUserRoleEnum, LocationType, UOMCategory } from 'app/models';
import { MerchantStoreOnboardingComponent, MerchantProductsComponent } from 'app/merchant';

enum MenuTab {
    ProductDetails = 1,
    Pricing = 2,
    UOM = 3,
    ProductAvailability = 4,
    Inventory = 5
}

@Component({
    selector: 'merchant-detail-product',
    templateUrl: './merchant-detail-product.component.html',
    styleUrls: ['./merchant-detail-product.component.scss',
        '../../../../../styles/sidebar.scss',
        '../../../../../styles/style-layout.scss'],
    providers: [MessageService]
})
export class MerchantDetailProductComponent implements OnInit {
    public productId: number = 0;
    @Input()
    set _productId(value: number) {
        this.productId = value;
        if (this.productId > 0) {
            this.getProductForEdit();
        }
    }

    public showMerchantDetailProductSideBar: boolean = false;
    @Input()
    set _showMerchantDetailProductSideBar(value: boolean) {
        this.showMerchantDetailProductSideBar = value;
    }

    @Input() public merchantStoreOnboardingComponent!: MerchantStoreOnboardingComponent;
    @Input() public merchantProductsComponent!: MerchantProductsComponent;

    @Output() changedShowMerchantDetailProductSideBarEmit = new EventEmitter<{ visible: boolean; needUpdateProducts: boolean }>();
    changedShowMerchantDetailProductSideBar(increased: any, productIsDeleted: boolean) {
        this.changedShowMerchantDetailProductSideBarEmit.emit({ visible: increased, needUpdateProducts: productIsDeleted });
    }

    constructor(private messageService: MessageService,
        public merchantService: MerchantService, private storeService: StoreService,
        public appData: AppDataService, private productService: ProductService) {
        this.isMerchant = this.appData.isMerchant();
    }

    public isMerchant: boolean = false;
    public xpressUserRoleEnum = XpressUserRoleEnum;

    public menuTab = MenuTab;
    public activeMenuTab: MenuTab = MenuTab.ProductDetails;
    public menuItems: MenuItem[] = [
        {
            label: 'Product details',
            id: `${MenuTab.ProductDetails}`,
            command: (e: any) => this.handleMenuTabChange(+e.item.id)
        },
        {
            label: 'Pricing',
            id: `${MenuTab.Pricing}`,
            command: (e: any) => this.handleMenuTabChange(+e.item.id)
        },
        {
            label: 'UOM',
            id: `${MenuTab.UOM}`,
            command: (e: any) => this.handleMenuTabChange(+e.item.id)
        },
        {
            label: 'Availability',
            id: `${MenuTab.ProductAvailability}`,
            command: (e: any) => this.handleMenuTabChange(+e.item.id)
        }
        //{
        //    label: 'Inventory',
        //    id: `${MenuTab.Inventory}`,
        //    command: (e: any) => this.handleMenuTabChange(+e.item.id)
        //}
    ];

    public loading: boolean = false;

    public product: any = {};
    public tags: any[] = [];
    public foundTags: any[] = [];
    public stores: any[] = [];
    public allCategories: any[] = [];
    public categories: any[] = [];
    public selectedParentCategory: any = undefined;

    public uomCategory = UOMCategory.UOMCategoryList;
    public selectedUOMCategory!: string;
    public locationType = LocationType.LocationTypeList;

    ngOnInit(): void {
        this.getAllCategories();
        this.getStoresByMerchant();
    }

    public getUOM() {
        return UOMCategory.getUOM(this.selectedUOMCategory);
    }

    public handleMenuTabChange(tab: MenuTab) {
        this.activeMenuTab = tab;
    }

    public getProductForEdit() {
        this.loading = true;

        const dto = {
            ProductId: this.productId
        };

        this.productService.getProductForEdit(dto, this.messageService).then(data => {
            if (!data) {
                this.loading = false;
                return;
            }

            if (!data.Success) {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                return;
            }
            this.selectedUOMCategory = UOMCategory.findUOM(data.Items[0].UnitId);
            this.product = data;
            this.getTags();
            this.selectedParentCategory = data.CategoryParentId;
            this.loading = false;
        });
    }

    private getAllCategories(): void {
        const dto = {
            ParentId: null,
            GetAll: true,
            MerchantId: null
        };
        this.productService.getCategories(dto, this.messageService)
            .then(data => {
                if (!data) {
                    return;
                }

                this.allCategories = data;
                this.categories = data;
            });
    }

    private getTags(): void {
        this.productService.getTags(null, this.messageService)
            .then(data => {
                if (!data) {
                    return;
                }

                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.tags = data.Items;
                this.product.Tags.forEach((p: any) => {
                    const foundTag = this.tags.find(s => s.Id === p);
                    if (foundTag !== null && foundTag !== undefined) {
                        this.foundTags.push(foundTag);
                    }
                });
            });
    }

    public handleParentCategoryChange(): void {
        this.getCategories();
    }

    public getStoresByMerchant() {
        const dto = {
            MerchantId: this.appData.authData.MerchantId
        };

        this.storeService.getStoresByMerchant(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.stores = data.Items;
            })
            .catch(err => {
                this.loading = false;
                throw err;
            });
    }

    private getCategories(): void {
        this.categories = [];
        for (const category of this.allCategories) {
            if (this.selectedParentCategory) {
                if (category.ParentId === this.selectedParentCategory.Id) {
                    this.categories.push(category);
                }
            }
        }

        if (this.categories.length === 0) {
            this.categories = this.allCategories;
        }
    }

    public deleteProduct(): void {
        this.loading = true;
        const dto = {
            ProductId: this.productId
        };
        this.productService.deleteProduct(dto, this.messageService)
            .then(data => {
                if (!data) {
                    this.loading = false;
                    return;
                }
                this.loading = false;

                this.showMerchantDetailProductSideBar = false;
                this.changedShowMerchantDetailProductSideBar(this.showMerchantDetailProductSideBar, true);

            })
            .catch(err => {
                this.loading = false;
                throw err;
            });
    }

    cancel() {
        this.changedShowMerchantDetailProductSideBar(!this.showMerchantDetailProductSideBar, false);
    }
}
